import { Stack } from 'component-library';
import { useEffect } from 'react';

import { IndicatorName, PlotReportFactLabelNameEnum, ThreatType } from '@/api/rest/resources/types/fact';
import { GraphTileType } from '@/api/rest/resources/types/widget';
import { ItemizedGraphTypeRenderer } from '@/components/Charts/components/ItemizedGraphType';
import { HabitatIntactnessThreatsBreakdownHistorical } from '@/components/Charts/HabitatIntactnessThreatsBreakdownHistorical';
import { NUM_ITEMS_TYPE } from '@/components/Charts/hooks/usePlotsForItemizedGraph';
import { GraphFact } from '@/components/Charts/types';
import { GraphTileProps } from '@/components/IndicatorCard/GraphTile';
import { useSetIndicatorHasData } from '@/components/IndicatorCard/IndicatorContextProvider';
import { useTrendLabelFromOrganizationFact } from '@/hooks/useTrendLabelFromFact';
import { useOrganizationIndicatorGraphFact } from '@/pages/shared/hooks/useOrganizationFact';
import { NoData } from '@/pages/shared/projects/pages/project/pages/sustainableProduction/components/NoData';

export const HabitatIntactnessThreatsBreakdownGraphTile = ({
  plotGroupId,
  graphType,
  zoom,
  setZoom,
  ...props
}: Partial<GraphTileProps>) => {
  const { setIndicatorGraphData } = useSetIndicatorHasData();

  const { [IndicatorName.threat_breakdown_total_historic_graph]: threatBreakdownHistoric } =
    useOrganizationIndicatorGraphFact<GraphFact[]>(
      IndicatorName.threat_breakdown_total_historic_graph,
      null,
      undefined,
      plotGroupId,
    );

  const trendLabel = useTrendLabelFromOrganizationFact({
    fact: IndicatorName.threat_breakdown_total_previous_5y_change,
  });

  const chartData = threatBreakdownHistoric?.value;

  const noChartData = !chartData?.length;

  useEffect(() => {
    setIndicatorGraphData({ graphData: !noChartData });
  }, [noChartData, setIndicatorGraphData]);

  if (noChartData) {
    return <NoData />;
  }

  return (
    <Stack center data-testid='habitat-intactness-threats-breakdown-graph-tile'>
      <Stack spacing={2.5} className='w-full items-center justify-center px-1 py-6 lg:items-start lg:p-8 lg:pl-1'>
        <ItemizedGraphTypeRenderer
          graphType={graphType}
          indicator={GraphTileType.historic_habitat_intactness_threats_breakdown}
          plotRenderType={props?.plotRenderType ?? NUM_ITEMS_TYPE.TOP_PLOTS}
          plotGroupId={plotGroupId ?? undefined}
          zoom={zoom}
          setZoom={setZoom}
          defaultGraphType={
            <HabitatIntactnessThreatsBreakdownHistorical
              data={chartData}
              trendLabel={trendLabel}
              plotGroupId={plotGroupId ?? undefined}
            />
          }
          labels={{
            [PlotReportFactLabelNameEnum.threat_type]: ThreatType.no_threat_area,
          }}
        />
      </Stack>
    </Stack>
  );
};
